import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  VideoCall,
  Group,
  Event,
  School,
  Favorite,
  Grass,
  Diamond,
  Search,
  BookOnline,
  Support,
  Email,
  Phone,
  Chat,
  Dashboard,
  Schedule,
  Payment,
  Settings,
  People,
  Star,
  TrendingUp,
  Wallet,
  Coffee,
  TheaterComedy,
  Create,
  Analytics,
  Notifications,
  Security,
  Help,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Styled components
const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
  color: "#fff",
  fontWeight: 700,
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "1rem",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 14px rgba(99,102,241,0.4)",
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  border: "1px solid #f0f0f0",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
  },
}));

const SuitCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  height: "100%",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 16px 48px rgba(0,0,0,0.15)",
  },
}));

const TalentHelpPage = () => {
  const navigate = useNavigate();
  const [expandedFAQ, setExpandedFAQ] = useState<number | false>(false);

  const handleFAQChange =
    (index: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFAQ(isExpanded ? index : false);
    };

  const suits = [
    {
      name: "Heart Suit",
      icon: <Favorite sx={{ fontSize: 40, color: "#e91e63" }} />,
      color: "#e91e63",
      description: "Live Entertainment & Interactive Shows",
      features: [
        "Live streaming sessions",
        "Interactive performances",
        "Real-time audience engagement",
        "Entertainment-focused content",
      ],
    },
    {
      name: "Spade Suit",
      icon: <School sx={{ fontSize: 40, color: "#2196f3" }} />,
      color: "#2196f3",
      description: "Learning & Educational Content",
      features: [
        "Educational workshops",
        "Skill development sessions",
        "Tutorial and training content",
        "Knowledge sharing platforms",
      ],
    },
    {
      name: "Clover Suit",
      icon: <Grass sx={{ fontSize: 40, color: "#4caf50" }} />,
      color: "#4caf50",
      description: "Events & Community Gatherings",
      features: [
        "Community events",
        "Social gatherings",
        "Event hosting and participation",
      ],
    },
    {
      name: "Diamond Suit",
      icon: <Diamond sx={{ fontSize: 40, color: "#ff9800" }} />,
      color: "#ff9800",
      description: "Commerce & Business Services",
      features: [
        "Product demonstrations",
        "Sales and marketing sessions",
        "Professional services",
      ],
    },
  ];

  const talentFeatures = [
    {
      title: "Dashboard & Analytics",
      icon: <Dashboard sx={{ fontSize: 40, color: "#6366F1" }} />,
      description: "Track your performance and earnings",
      features: [
        "Real-time performance metrics",
        "Revenue tracking and analytics",
        "Booking statistics",
        "Review and rating overview",
        "Today's schedule view",
      ],
    },
    {
      title: "Card Management",
      icon: <Create sx={{ fontSize: 40, color: "#9F03FF" }} />,
      description: "Create and manage your service cards",
      features: [
        "Create cards across all four suits",
        "Set pricing and duration",
        "Upload media and descriptions",
        "Manage card status (Active/Paused)",
        "Edit and update card details",
      ],
    },
    {
      title: "Schedule Management",
      icon: <Schedule sx={{ fontSize: 40, color: "#FF6B35" }} />,
      description: "Configure your availability and time slots",
      features: [
        "Set weekly availability patterns",
        "Configure time slots for bookings",
        "Manage buffer times between sessions",
        "Calendar integration",
        "Real-time availability updates",
      ],
    },
    {
      title: "Booking Management",
      icon: <BookOnline sx={{ fontSize: 40, color: "#34D399" }} />,
      description: "Handle incoming booking requests",
      features: [
        "View pending booking requests",
        "Accept or reject bookings",
        "Manage booking status",
        "Handle group and solo sessions",
        "StageCard booking management",
      ],
    },
    {
      title: "Coffee Calls",
      icon: <Coffee sx={{ fontSize: 40, color: "#6F4E37" }} />,
      description: "Quick consultation sessions",
      features: [
        "Enable/disable coffee call availability",
        "Set coffee call pricing",
        "Configure coffee call duration",
        "Manage coffee call requests",
        "Quick consultation sessions",
      ],
    },
    {
      title: "StageMode",
      icon: <TheaterComedy sx={{ fontSize: 40, color: "#FFD700" }} />,
      description: "Large-scale live events and performances",
      features: [
        "Create StageMode events",
        "Large audience live streaming",
        "Event management tools",
        "Backstage panel access",
        "Featured event hosting",
      ],
    },
    {
      title: "GTP (Group Talent Pool)",
      icon: <People sx={{ fontSize: 40, color: "#8B5CF6" }} />,
      description: "Collaborate with other talents",
      features: [
        "Create or join talent groups",
        "Group performance sessions",
        "Revenue sharing management",
        "Member management",
        "Group profile creation",
      ],
    },
    {
      title: "Wallet & Payments",
      icon: <Wallet sx={{ fontSize: 40, color: "#10B981" }} />,
      description: "Manage your earnings and transactions",
      features: [
        "Track earnings and revenue",
        "View transaction history",
        "Chip wallet management",
        "Payment processing",
        "Financial analytics",
      ],
    },
  ];

  const sessionTypes = [
    {
      title: "One-on-One Sessions",
      icon: <VideoCall sx={{ fontSize: 40, color: "#6366F1" }} />,
      description: "Personalized private sessions with users",
      features: [
        "Direct video calls with users",
        "Personalized attention",
        "Custom session duration",
        "Private consultation space",
      ],
    },
    {
      title: "Group Sessions",
      icon: <Group sx={{ fontSize: 40, color: "#9F03FF" }} />,
      description: "Interactive group experiences with multiple participants",
      features: [
        "Multi-participant sessions",
        "Group learning experiences",
        "Shared interaction space",
        "Community building",
      ],
    },
    {
      title: "StageMode Sessions",
      icon: <Event sx={{ fontSize: 40, color: "#FF6B35" }} />,
      description: "Large audience live events and performances",
      features: [
        "Large-scale live events",
        "Audience participation",
        "Live streaming capabilities",
        "Event management tools",
      ],
    },
    {
      title: "Coffee Calls",
      icon: <Chat sx={{ fontSize: 40, color: "#34D399" }} />,
      description: "Quick consultation and networking sessions",
      features: [
        "Short duration calls",
        "Quick consultations",
        "Networking opportunities",
        "Flexible scheduling",
      ],
    },
  ];

  const faqs = [
    {
      question: "How do I create my first card?",
      answer:
        "To create your first card: 1) Navigate to 'Cards' in your dashboard, 2) Click 'Create New Card', 3) Choose your suit (Heart, Spade, Clover, or Diamond), 4) Fill in the card details including title, description, pricing, and duration, 5) Upload media content, 6) Set your availability, 7) Review and publish your card.",
    },
    {
      question: "How do I manage my schedule and availability?",
      answer:
        "Go to 'Schedules' in your dashboard to manage your availability. You can set weekly patterns, configure time slots, add buffer times between sessions, and view your calendar. The system will automatically show your available slots to potential users based on your settings.",
    },
    {
      question: "How do I handle booking requests?",
      answer:
        "When you receive a booking request, you'll see it in your 'Bookings' section. You can review the request details, user information, and session requirements. You can then accept, reject, or request modifications. Accepted bookings will appear in your schedule.",
    },
    {
      question: "What are Coffee Calls and how do I enable them?",
      answer:
        "Coffee Calls are quick 15-30 minute consultation sessions. To enable them: 1) Go to your profile settings, 2) Toggle on 'Coffee Call Availability', 3) Set your coffee call price and duration, 4) Users can then request quick calls with you for immediate consultation.",
    },
    {
      question: "How does StageMode work?",
      answer:
        "StageMode allows you to host large-scale live events. Create a StageMode card with event details, set venue size (Small/Medium/Large/Broadcast), configure pricing, and schedule your event. You'll have access to backstage tools and can manage large audiences during live streaming.",
    },
    {
      question: "What is GTP (Group Talent Pool)?",
      answer:
        "GTP allows you to collaborate with other talents. You can create or join talent groups, perform together, and share revenue. Set up revenue sharing rules, manage group members, and create group profiles. This is perfect for bands, comedy groups, or collaborative performances.",
    },
    {
      question: "How do I track my earnings and performance?",
      answer:
        "Your dashboard provides comprehensive analytics including earnings, booking statistics, performance metrics, and reviews. The wallet section shows detailed transaction history, and you can track revenue from different session types and suits.",
    },
    {
      question: "How do I manage my profile and settings?",
      answer:
        "Access your profile settings to update personal information, bio, skills, portfolio links, and contact preferences. You can also manage notification settings, security preferences, and account settings from the settings menu.",
    },
    {
      question: "What are the different suits and how do I choose?",
      answer:
        "The four suits categorize your content: Heart (Entertainment), Spade (Learning), Clover (Events), and Diamond (Commerce). Choose based on your content type. You can create cards in multiple suits to diversify your offerings.",
    },
    {
      question: "How do I handle technical issues during sessions?",
      answer:
        "If you encounter technical issues during a session, you can use the platform's built-in troubleshooting tools. For persistent issues, contact our support team through the help section. We recommend testing your setup before sessions.",
    },
    {
      question: "How do I manage user requests for custom cards?",
      answer:
        "Users can request custom cards through the 'User Requested Cards' section. You can review these requests, claim ones that interest you, and create custom cards based on user specifications. This helps you offer personalized services.",
    },
    {
      question: "What payment methods are supported?",
      answer:
        "StageLink supports various payment methods including credit/debit cards and digital wallets. All transactions are processed securely. You'll receive payments in your wallet, and you can track all transactions in the wallet section.",
    },
  ];

  const quickActions = [
    {
      title: "Create New Card",
      icon: <Create sx={{ fontSize: 30, color: "#6366F1" }} />,
      action: () => navigate("/create-card"),
    },
    {
      title: "Manage Schedule",
      icon: <Schedule sx={{ fontSize: 30, color: "#9F03FF" }} />,
      action: () => navigate("/schedules"),
    },
    {
      title: "View Bookings",
      icon: <BookOnline sx={{ fontSize: 30, color: "#FF6B35" }} />,
      action: () => navigate("/bookings"),
    },
    {
      title: "Dashboard",
      icon: <Dashboard sx={{ fontSize: 30, color: "#34D399" }} />,
      action: () => navigate("/talent-dashboard"),
    },
    {
      title: "Wallet",
      icon: <Wallet sx={{ fontSize: 30, color: "#10B981" }} />,
      action: () => navigate("/wallet"),
    },
    {
      title: "GTP Groups",
      icon: <People sx={{ fontSize: 30, color: "#8B5CF6" }} />,
      action: () => navigate("/gtp"),
    },
    {
      title: "StageMode",
      icon: <TheaterComedy sx={{ fontSize: 30, color: "#FFD700" }} />,
      action: () => navigate("/stage-mode"),
    },
    {
      title: "Settings",
      icon: <Settings sx={{ fontSize: 30, color: "#6B7280" }} />,
      action: () => navigate("/settings"),
    },
  ];

  return (
    <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: "#333",
              mb: 2,
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Talent Help Center
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "#666", mb: 4, maxWidth: "600px", mx: "auto" }}
          >
            Everything you need to know about managing your talent profile, creating cards, handling bookings, and maximizing your success on StageLink
          </Typography>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Quick Actions
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {quickActions.map((action, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 3,
                    cursor: "pointer",
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                    },
                  }}
                  onClick={action.action}
                >
                  <Box sx={{ mb: 2 }}>{action.icon}</Box>
                  <Typography variant="body2" fontWeight={600}>
                    {action.title}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Talent Features */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Your Talent Toolkit
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "#666",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Discover all the powerful features available to help you succeed as a talent on StageLink.
          </Typography>
          <Grid container spacing={4}>
            {talentFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard>
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
                      {feature.description}
                    </Typography>
                    <Box>
                      {feature.features.map((feat, featureIndex) => (
                        <Typography
                          key={featureIndex}
                          variant="body2"
                          sx={{
                            color: "#555",
                            mb: 1,
                            fontSize: "0.875rem",
                          }}
                        >
                          • {feat}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Suits Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Understanding Our Suits
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "#666",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            StageLink organizes content into four distinct suits, each offering unique opportunities for different types of talents.
          </Typography>
          <Grid container spacing={4}>
            {suits.map((suit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <SuitCard>
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      backgroundColor: `${suit.color}15`,
                      height: "100%",
                      }}
                  >
                    <Box sx={{ mb: 2 }}>{suit.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mb: 1, color: suit.color }}
                    >
                      {suit.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
                      {suit.description}
                    </Typography>
                    <Box>
                      {suit.features.map((feature, featureIndex) => (
                        <Chip
                          key={featureIndex}
                          label={feature}
                          size="small"
                          sx={{
                            m: 0.5,
                            backgroundColor: `${suit.color}20`,
                            color: suit.color,
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </SuitCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Session Types */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Session Types You Can Offer
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "#666",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Choose from different session formats to match your expertise and audience preferences.
          </Typography>
          <Grid container spacing={4}>
            {sessionTypes.map((session, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <FeatureCard>
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Box sx={{ mb: 2 }}>{session.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      {session.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
                      {session.description}
                    </Typography>
                    <Box>
                      {session.features.map((feature, featureIndex) => (
                        <Typography
                          key={featureIndex}
                          variant="body2"
                          sx={{
                            color: "#555",
                            mb: 1,
                            fontSize: "0.875rem",
                          }}
                        >
                          • {feature}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Getting Started Steps */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Getting Started as a Talent
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                step: "1",
                title: "Complete Your Profile",
                description: "Set up your talent profile with bio, skills, and portfolio",
                icon: "👤",
              },
              {
                step: "2",
                title: "Create Your First Card",
                description: "Choose a suit and create your first service card",
                icon: "🎯",
              },
              {
                step: "3",
                title: "Set Your Schedule",
                description: "Configure your availability and time slots",
                icon: "📅",
              },
              {
                step: "4",
                title: "Start Accepting Bookings",
                description: "Review and accept booking requests from users",
                icon: "✅",
              },
              {
                step: "5",
                title: "Host Your Sessions",
                description: "Conduct live sessions and earn revenue",
                icon: "🎉",
              },
              {
                step: "6",
                title: "Grow Your Business",
                description: "Expand with multiple cards, GTP groups, and StageMode",
                icon: "🚀",
              },
            ].map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 3,
                    borderRadius: "16px",
                    height: "100%",
                    position: "relative",
                    overflow: "visible",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -20,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: "#6366F1",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "1.2rem",
                    }}
                  >
                    {step.step}
                  </Box>
                  <Box sx={{ mt: 2, mb: 2, fontSize: "3rem" }}>{step.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {step.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
          >
            Frequently Asked Questions
          </Typography>
          <Box sx={{ maxWidth: "800px", mx: "auto" }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expandedFAQ === index}
                onChange={handleFAQChange(index)}
                disableGutters
                elevation={0}
                sx={{
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#6366F1" }} />}
                  sx={{
                    px: 3,
                    py: 2,
                    borderLeft: "4px solid #6366F1",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 2 }}>
                  <Typography sx={{ color: "#555", fontSize: "0.95rem" }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        {/* Contact Support */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "20px",
            p: 4,
            textAlign: "center",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Need More Help?
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#666", mb: 4, maxWidth: "600px", mx: "auto" }}
          >
            Our support team is here to help you succeed on StageLink. Reach out to us through any of these channels.
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {[
              {
                icon: <Email />,
                title: "Email Support",
                contact: "talent-support@taasnet.com",
              },
              {
                icon: <Phone />,
                title: "Phone Support",
                contact: "+1 (555) 123-4567",
              },
              { icon: <Chat />, title: "Live Chat", contact: "Available 24/7" },
            ].map((contact, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Box sx={{ textAlign: "center" }}>
                  <Box sx={{ mb: 2, color: "#6366F1" }}>{contact.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {contact.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {contact.contact}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 4 }}>
            <GradientButton
              onClick={() => navigate("/contact")}
              startIcon={<Email />}
            >
              Contact Talent Support
            </GradientButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TalentHelpPage;